import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { useOTPSender } from '@/features/auth/otp';
import { normalizePhoneNumber } from '@/lib/utils/phone';
import { checkUserExists } from '@/services/user';

// const identifierSchema = z.union([
//   z.string().email(),
//   z.string().regex(/^(06|07)\d{8}$/, 'Invalid phone number'),
// ]);

interface UseSignInOptions {
  onSuccess: (data: {
    identifier: string;
    isEmail: boolean;
    hasExistingOTP?: boolean;
    timeLeft?: number;
  }) => void;
  onUserNotFound?: (identifier: string, isEmail: boolean) => void;
}

export function useSignIn(options: UseSignInOptions) {
  const { onSuccess, onUserNotFound } = options;
  const { t } = useTranslation();
  const { sendOTP } = useOTPSender();

  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateIdentifier = (value: string): { isValid: boolean; isEmail: boolean } => {
    const normalizedValue = value.replace(/\s/g, '');

    // Try email validation first
    const emailResult = z.string().email().safeParse(normalizedValue);
    if (emailResult.success) {
      return { isValid: true, isEmail: true };
    }

    // Try phone validation (French 06/07 or international E.164)
    const phoneResult = z
      .string()
      .regex(/^(?:(?:\+|00)?[1-9]\d{6,14}|0[1-9]\d{8})$/)
      .safeParse(normalizedValue);
    if (phoneResult.success) {
      return { isValid: true, isEmail: false };
    }

    return { isValid: false, isEmail: false };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!identifier.trim()) {
      setError(t('formErrors.required'));
      return;
    }

    const validation = validateIdentifier(identifier);
    if (!validation.isValid) {
      setError(t('dashboard.auth.signIn.invalidIdentifier'));
      return;
    }

    const isEmail = validation.isEmail;
    setIsSubmitting(true);

    try {
      // Normalize identifier based on type
      const normalizedIdentifier = isEmail ? identifier.trim() : normalizePhoneNumber(identifier);

      const devBypassPhone = import.meta.env.VITE_DEV_USER_PHONE;
      const isDevBypass =
        !isEmail &&
        !!devBypassPhone &&
        normalizePhoneNumber(identifier) === normalizePhoneNumber(devBypassPhone);

      if (!isDevBypass) {
        const userExists = await checkUserExists(normalizedIdentifier, isEmail);

        if (!userExists.exists) {
          setIsSubmitting(false);
          if (onUserNotFound) {
            onUserNotFound(normalizedIdentifier, isEmail);
          } else {
            setError(t('dashboard.auth.signIn.userNotFound'));
          }
          return;
        }
      }

      const result = await sendOTP({
        identifier: normalizedIdentifier,
        isEmail,
        checkExisting: true,
        enforceRateLimit: false,
      });

      if (result.wasSent) {
        const method = isEmail
          ? t('dashboard.auth.signIn.byEmail')
          : t('dashboard.auth.signIn.bySMS');
        toast.success(
          t('dashboard.auth.signIn.otpSent', { method, identifier: normalizedIdentifier }),
          {
            duration: 4000,
          },
        );
      }

      onSuccess({
        identifier: normalizedIdentifier,
        isEmail,
        hasExistingOTP: result.hasExistingOtp,
        timeLeft: result.timeLeft,
      });
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err instanceof Error ? err.message : t('dashboard.auth.signIn.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    identifier,
    setIdentifier,
    error,
    isSubmitting,
    handleSubmit,
  };
}
